import React, { useEffect, useState } from "react";

import { DatasetsApi } from "api/DatasetsApi";
import { useGetRecentData } from "api/api-hooks/datasets-hooks";
import ChartInDialogButton from "components/basic/chart-in-dialog-button/ChartInDialogButton";
import CopyTextWithButton from "components/basic/copy-text-with-button/CopyTextWithButton";
import {
  AutoComplete,
  AutoCompleteChangeEvent,
  AutoCompleteCompleteEvent,
} from "primereact/autocomplete";
import { T } from "services/lang/LanguageService";
import { timeAgo } from "utils/time-utils";
import { isDatasetIdentifier } from "utils/utils";
import "./style.scss";

interface IDatasetDropdownProps {
  value?: string | null;
  onChange: (e: string | null, label: string | null) => void;
  style?: React.CSSProperties;
  disabled?: boolean;
}

interface DropdownItem {
  value: string | number;
  label: string;
}

// eslint-disable-next-line import/prefer-default-export
export function DatasetDropdown(props: IDatasetDropdownProps) {
  const [selectedDataset, setSelectedDataset] = useState<DropdownItem | null>(null);
  const [filteredDatasets, setFilteredDatasets] = useState<DropdownItem[]>([]);

  const recentDataQuery = useGetRecentData([props.value!], { options: { enabled: !!props.value } });
  useEffect(() => {
    if (recentDataQuery.data !== undefined && recentDataQuery.data.length > 0) {
      const dataset = recentDataQuery.data[0]!!;
      const label = `${dataset.title} `;

      if (dataset.data.length > 0) {
        const { key: time, value } = dataset.data[0];
        setSelectedDataset({
          value: dataset.identifier,
          label: `${label} | ${value.toFixed(2)} ${dataset.unit} | ${timeAgo(time)}`,
        });
      } else {
        setSelectedDataset({ value: dataset.identifier, label: `${label} | ${dataset.unit}` });
      }
    }
  }, [recentDataQuery.data]);

  const searchDatasets = (e: AutoCompleteCompleteEvent) => {
    const searchStr = e.query.trim();

    if (searchStr.length < 3) return;

    let queryParams: [string, number, string[]] = [searchStr, 100, []];
    if (isDatasetIdentifier(searchStr)) {
      queryParams = ["", 20, [searchStr]];
    }
    DatasetsApi.queryDatasets(...queryParams).then(r => {
      if (!r.error && r.data) {
        const selections = r.data.map(
          it => ({ value: it.identifier, label: it.toString() }) as DropdownItem
        );
        setFilteredDatasets(selections);
      }
    });
  };

  const onChange = (e: AutoCompleteChangeEvent) => {
    setSelectedDataset(e.value);
    if (typeof e.value === "string") return; // Ignore string auto complete dumb input

    props.onChange(e.value?.value || null, e.value?.label || null);
  };

  return (
    <div className="p-inputgroup flex w-full dataset-search">
      <AutoComplete
        placeholder={T("label.type-to-search-datasets")}
        value={selectedDataset}
        suggestions={filteredDatasets}
        onChange={onChange}
        completeMethod={searchDatasets}
        minLength={3}
        field="label"
        onClear={() => {
          setSelectedDataset(null);
          props.onChange(null, null);
        }}
        style={{ width: "100%", ...props.style }}
        disabled={props.disabled}
      />
      {props.value && (
        <div className="p-inputgroup-addon">
          <CopyTextWithButton text="" copyableText={props.value} />
          <ChartInDialogButton identifier={props.value} header={T("word.dataset")} />
        </div>
      )}
    </div>
  );
}
